import React from 'react';
import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';

import { SerializedStyles, Global, css } from '@emotion/core';
import { ThemeProvider as MUIThemeProvider } from '@material-ui/styles';
import { ThemeProvider as EmotionThemeProvider } from 'emotion-theming';
import createTheme, { Theme } from './theme';
import { normalText } from './mixins';
import useQueryParameters from 'hooks/useQueryParameters';

const styles = (theme: Theme): SerializedStyles => {
  const { fontFamily } = theme.mui.typography;

  return css`
    html {
      /*
       * Using 10px simplification:
       * https://www.sitepoint.com/understanding-and-using-rem-units-in-css/
      */
      font-size: 62.5%;
      font-family: ${fontFamily};
    }
    body {
      text-size-adjust: 100%;
    }

    body,
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0;
    }
    body {
      // Prevent margin collapsing.
      //
      // When the first child has a margin, and the body doesn't, the child's
      // margin collapsed into the body's, and so the body's height doesn't
      // account for the child's margin. To report the correct height to the
      // iframe's parent, we need the body's height to count everything.
      //
      // The normal way to prevent margin collapse is by adding something
      // between the child's margin and the parent's margin, such as a 1px
      // padding or border. Since we have a navigation bar, these options
      // create an unsightly gap at the top.
      //
      // Margin for inline-block element are _not_ collapsed. So we make the
      // body inline-block. The width has to be specified as 100%, otherwise
      // the width of the body only takes up as much space as its children
      // need, and everything will be aligned to the left.
      display: inline-block;
      width: 100%;
    }

    p,
    button {
      ${normalText(theme)}
    }

    a {
      color: ${theme.mui.palette.primary.main};
      font-style: italic;
      text-decoration: underline;
    }
  `;
};

const ThemeProvider: React.FC = props => {
  const { children } = props;
  const tenant = useSelector(selectTenant);
  const queryParams = useQueryParameters();
  const theme = createTheme(tenant, queryParams);

  return (
    <EmotionThemeProvider theme={theme}>
      <Global styles={styles} />
      <MUIThemeProvider theme={theme.mui}>{children}</MUIThemeProvider>
    </EmotionThemeProvider>
  );
};

export default ThemeProvider;
